import { useQuery } from "react-query";
import BackButton from "./BackButton";
import { LoadingOverlay, Select, Tabs, Title } from "@mantine/core";
import axios from "axios";
import { useParams } from "react-router-dom";
import RecentGamesTable from "./RecentGamesTable";
import { EndReason, GameDuration, PlayerColor, TimeControl } from "./models";
import RatedStats from "./RatedStats";
import AverageEvalStats from "./AverageEvalStats";
import WinDrawLossStats from "./WinDrawLossStats";
import { useViewportSize } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { getUser, redirectToPremiumIfNotPremium } from "./Authentication";

export interface OtPosition {
    id: number;
    fen: string;
    name: string;
    userId: number;
    createdAt: string;
    updatedAt: string;
    index: number;
    eco: string;
    defaultPlayerColor: string;
    pgn: string;
    rating: string;
    OtAttempt2: OtAttempt2[];
    startRating?: number;
}

export interface OtAttempt2 {
    id: number;
    createdAt: string;
    updatedAt: string;
    userId: number;
    ot_positionId: number;
    evaluation: string;
    pgn: string;
    type: string;
    positionRating: string | number;
    engineLevel: string;
    ratingGain: string;
    playerColor: PlayerColor;
    endReason?: EndReason;
    startRating?: number;
    timeControls: TimeControl[];
    rejectionThreshold: number;
    gameDuration: GameDuration;
    isRated: boolean;
    selectedEngineLevel?: string;
}

const fetchStats = async (positionId: string): Promise<OtPosition> => {
    const positionStats = await axios.get(`${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_position_stats`, {
        withCredentials: true,
        params: {
            positionId,
        },
    });

    return positionStats.data;
};

const PositionStats = () => {
    const { width } = useViewportSize();
    const { positionId } = useParams();
    const { data: opening, isLoading } = useQuery(["positionStats"], () => fetchStats(positionId as string));
    const [filteredOpening, setFilteredOpening] = useState<OtPosition>();

    const { data: user } = useQuery(["user"], () => getUser());

    redirectToPremiumIfNotPremium(user);

    useEffect(() => {
        if (opening) {
            setFilteredOpening(opening);
        }
    }, [opening]);

    if (!opening || !filteredOpening) {
        return;
    }

    if (isLoading || !width) {
        return <LoadingOverlay />;
    }

    const handleChangeDateRange = (option: string) => {
        switch (option) {
            case "All time":
                setFilteredOpening(opening);
                break;
            case "Today":
                setFilteredOpening({
                    ...opening,
                    OtAttempt2: opening.OtAttempt2.filter(
                        (attempt) => new Date(attempt.createdAt).getDate() === new Date().getDate()
                    ),
                });
                break;
            case "Last 3 days":
                setFilteredOpening({
                    ...opening,
                    OtAttempt2: opening.OtAttempt2.filter(
                        (attempt) => new Date(attempt.createdAt).getDate() >= new Date().getDate() - 3
                    ),
                });
                break;
            case "Last 7 days":
                setFilteredOpening({
                    ...opening,
                    OtAttempt2: opening.OtAttempt2.filter(
                        (attempt) => new Date(attempt.createdAt).getDate() >= new Date().getDate() - 7
                    ),
                });
                break;
            case "Last 30 days":
                setFilteredOpening({
                    ...opening,
                    OtAttempt2: opening.OtAttempt2.filter(
                        (attempt) => new Date(attempt.createdAt).getDate() >= new Date().getDate() - 30
                    ),
                });
                break;
            default:
                break;
        }
    };

    return (
        <div className="my-32">
            <div className="space-y-12">
                <BackButton url={`/stats`} />
                <Title>{opening.name}</Title>
                <div></div>

                <Tabs defaultValue="rating">
                    <Tabs.List className="mb-4">
                        <Tabs.Tab value="rating">Rating</Tabs.Tab>
                        <Tabs.Tab value="eval">Average eval</Tabs.Tab>
                        <Tabs.Tab value="wdl">Win/draw/loss</Tabs.Tab>
                        <Select
                            className="ml-auto"
                            label=""
                            placeholder="Pick value"
                            data={["All time", "Today", "Last 3 days", "Last 7 days", "Last 30 days"]}
                            defaultValue={"All time"}
                            allowDeselect={false}
                            onChange={(option) => {
                                handleChangeDateRange(option as string);
                            }}
                        />
                    </Tabs.List>

                    <Tabs.Panel pt="xl" value="rating">
                        <RatedStats opening={filteredOpening} width={width} />
                    </Tabs.Panel>

                    <Tabs.Panel pt="xl" value="eval">
                        <AverageEvalStats opening={filteredOpening} />
                    </Tabs.Panel>

                    <Tabs.Panel pt="xl" value="wdl">
                        <WinDrawLossStats opening={filteredOpening} />
                    </Tabs.Panel>
                </Tabs>
            </div>
            <RecentGamesTable attempts={filteredOpening.OtAttempt2.slice().reverse()} opening={filteredOpening} />
        </div>
    );
};

export default PositionStats;
